import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Video, Calendar, Clock, Users, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { registerWebinar } from '@/lib/api';

const webinarTopics = [
  { id: 'erp-overview', title: 'Introduction to ERP Systems', date: 'Every Saturday', time: '10:00 AM EST' },
  { id: 'ifs-intro', title: 'Getting Started with IFS', date: 'Every Tuesday', time: '2:00 PM EST' },
  { id: 'odoo-basics', title: 'Odoo for Beginners', date: 'Every Wednesday', time: '11:00 AM EST' },
  { id: 'oracle-cloud', title: 'Oracle Cloud Overview', date: 'Every Thursday', time: '3:00 PM EST' },
  { id: 'netsuite-demo', title: 'NetSuite Demo Session', date: 'Every Friday', time: '1:00 PM EST' },
  { id: 'career-consult', title: 'ERP Career Consultation', date: 'On Request', time: 'Flexible' },
];

export default function WebinarPage() {
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', webinar_topic: '' });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.webinar_topic) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await registerWebinar(formData);
      setRegistered(true);
      toast.success('Registration successful! Check your email for details.');
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="webinar-success">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-brand-600" />
          </div>
          <h1 className="text-3xl font-bold text-brand-900 mb-4">Registration Successful!</h1>
          <p className="text-slate-600 mb-8">Thank you for registering. We've sent a confirmation email with the webinar details and joining link.</p>
          <div className="flex flex-col gap-4">
            <Link to="/programs"><Button className="btn-primary w-full">Explore Programs</Button></Link>
            <Button variant="outline" onClick={() => { setRegistered(false); setFormData({ first_name: '', last_name: '', email: '', phone: '', company: '', webinar_topic: '' }); }}>Register for Another</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="webinar-page">
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div  >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">Free Webinars</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">Join Our Free ERP Webinars</h1>
              <p className="text-lg text-slate-600 mb-8">Experience our training quality first-hand. Join a free webinar to learn about ERP systems, career paths, and how our programs can help you succeed.</p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Video, text: 'Live interactive sessions' },
                  { icon: Users, text: 'Q&A with experts' },
                  { icon: Calendar, text: 'Multiple topics available' },
                  { icon: Clock, text: 'Recorded for later viewing' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5 text-brand-600" />
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div   >
              <Card className="shadow-lg">
                <CardHeader><CardTitle>Register for Free Webinar</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input id="first_name" value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} data-testid="webinar-first-name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input id="last_name" value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} data-testid="webinar-last-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} data-testid="webinar-email" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" value={formData.company} onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Select Webinar Topic *</Label>
                      <Select value={formData.webinar_topic} onValueChange={(value) => setFormData(prev => ({ ...prev, webinar_topic: value }))}>
                        <SelectTrigger data-testid="webinar-topic-select"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                        <SelectContent>
                          {webinarTopics.map((topic) => (
                            <SelectItem key={topic.id} value={topic.id}>{topic.title} - {topic.date}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="webinar-submit-btn">
                      {loading ? 'Registering...' : 'Register Now - Free'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container-main">
          <h2 className="section-heading mb-12 text-center">Upcoming Webinars</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {webinarTopics.map((topic, index) => (
              <div key={topic.id}    >
                <Card className="program-card h-full">
                  <CardContent className="pt-6">
                    <Video className="w-8 h-8 text-brand-600 mb-4" />
                    <h3 className="font-heading font-semibold text-lg text-brand-900 mb-2">{topic.title}</h3>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p><Calendar className="w-4 h-4 inline mr-2" />{topic.date}</p>
                      <p><Clock className="w-4 h-4 inline mr-2" />{topic.time}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
